import { useState, useRef, useEffect, useCallback } from 'react';


/*
{
  aspectRatio: true;
  autoGainControl: true;
  brightness: true;
  channelCount: true;
  colorTemperature: true;
  contrast: true;
  deviceId: true;
  echoCancellation: true;
  exposureCompensation: true;
  exposureMode: true;
  facingMode: true;
  focusMode: true;
  frameRate: true;
  groupId: true;
  height: true;
  iso: true;
  latency: true;
  noiseSuppression: true;
  pointsOfInterest: true;
  sampleRate: true;
  sampleSize: true;
  saturation: true;
  sharpness: true;
  torch: true;
  volume: true;
  whiteBalanceMode: true;
  width: true;
  zoom: true;
}

*/


export default function useAudioRecorder(maxDuration = null) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  const isRecordingCapable = () => {
    if (navigator.mediaDevices === undefined) return false;
    return true;
  };

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  }, []);

  const startRecording = async () => {
    try {
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      setDuration(0);
      setRecordedBlob(null);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        setDuration((prev) => {
          const next = prev + 1;

          // ⛔ Stop when reaching maxDuration
          if (maxDuration && next >= maxDuration) {
            stopRecording();
          }

          return next;
        });
      }, 1000);

    } catch (err) {
      console.error('Microphone access error:', err);
      setError('Microphone access denied or unsupported');
    }
  };

  const reset = () => {
    setRecordedBlob(null);
    setDuration(0);
    setIsRecording(false);
  };

  return {
    isRecording,
    duration,
    recordedBlob,
    isRecordingCapable,
    error,
    startRecording,
    stopRecording,
    reset
  };
}